import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import BankAccountFieldset from "./BankAccountFieldset";

const simNaoOptions = [
  { value: "1", label: "Sim" },
  { value: "2", label: "Não" },
];

const specificPaymentRuleOptions = [
  { value: "2", label: "Não" },
  { value: "1", label: "Sim" },
];

const sectionTitleSx = {
  color: "#7A7A7A",
  fontWeight: "600",
  fontSize: "14px",
  my: 0.5,
};

export default function BankCommunication({ isStaff }) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const specificPaymentRule = watch("specificPaymentRule");
  const bankCommunicationIsValidated = watch("bankCommunicationIsValidated");

  const { fields: bankAccountsFields, append: appendBankAccount, remove: removeBankAccount } = useFieldArray({
    control,
    name: "bankAccounts",
  });

  const emptyBankAccount = {
    cnpj: "", bank: "", agency: "", account: "", agreementCode: "",
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        mx: { xs: 0, sm: 1, md: 2 },
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Pagamento FOPAG - Comunicação Bancária
        </Typography>

        {isStaff && (
          <Controller
            name="bankCommunicationIsValidated"
            control={control}
            render={({ field }) => (
              <FormControl required error={!!errors.bankCommunicationIsValidated}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox {...field} checked={!!field.value} size="small" />
                  <Typography variant="body2">Validado</Typography>
                </Box>
                <FormHelperText>
                  {errors.bankCommunicationIsValidated?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        )}
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <Controller
            name="hasBankTransferFile"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                size="small"
                required error={!!errors.hasBankTransferFile}
              >
                <InputLabel id="has-bank-transfer-file-label">
                  A empresa efetua os pagamentos via arquivo de transferência
                  bancária (CNAB)
                </InputLabel>
                <Select
                  {...field}
                  required disabled={bankCommunicationIsValidated}
                  labelId="has-bank-transfer-file-label"
                  label="A empresa efetua os pagamentos via arquivo de transferência bancária (CNAB)"
                >
                  {simNaoOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.hasBankTransferFile?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="bankFileLayoutD1"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                size="small"
                required error={!!errors.bankFileLayoutD1}
              >
                <InputLabel id="bank-file-layout-d1-label">
                  Layout arquivo bancário é regra de pagamento D+1
                </InputLabel>
                <Select
                  {...field}
                  required disabled={bankCommunicationIsValidated}
                  labelId="bank-file-layout-d1-label"
                  label="Layout arquivo bancário é regra de pagamento D+1"
                >
                  {simNaoOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.bankFileLayoutD1?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
          <Typography
            sx={{
              fontSize: "8px",
              color: "#7A7A7A",
              fontWeight: "400",
              ml: 1,
              my: 1,
            }}
          >
            (*) O crédito dos valores na conta do beneficiário é processado no
            prazo de um dia útil após o débito correspondente.
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="bankSequentialNumberRule"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                size="small"
                required error={!!errors.bankSequentialNumberRule}
              >
                <InputLabel id="bank-sequential-number-rule-label">
                  Regra de número sequencial
                </InputLabel>
                <Select
                  {...field}
                  required disabled={bankCommunicationIsValidated}
                  labelId="bank-sequential-number-rule-label"
                  label="Regra de número sequencial"
                >
                  {simNaoOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.bankSequentialNumberRule?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="issueUnifiedBankFile"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                size="small"
                required error={!!errors.issueUnifiedBankFile}
              >
                <InputLabel id="issue-unified-bank-file-label">
                  Emite arquivo unificado quando tem pagamentos na mesma data,
                  ex.: Férias e Rescisão
                </InputLabel>
                <Select
                  {...field}
                  required disabled={bankCommunicationIsValidated}
                  labelId="issue-unified-bank-file-label"
                  label="Emite arquivo unificado quando tem pagamentos na mesma data, ex.: Férias e Rescisão"
                >
                  {simNaoOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.issueUnifiedBankFile?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {bankAccountsFields.map((field, index) => (
          <Grid item xs={12} key={field.id}>
            <BankAccountFieldset
              index={index}
              disabled={bankCommunicationIsValidated}
              onRemove={() => removeBankAccount(index)}
              canRemove={bankAccountsFields.length > 1}
            />
          </Grid>
        ))}
        {!bankCommunicationIsValidated && (
          <Grid item xs={12}>
            <Button variant="outlined" size="small" onClick={() => appendBankAccount(emptyBankAccount)}>
              Adicionar outro banco
            </Button>
          </Grid>
        )}

        {/* specificPaymentRule — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Controller
                name="specificPaymentRule"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    required error={!!errors.specificPaymentRule}
                  >
                    <InputLabel id="specific-payment-rule-label">
                      Outro CNPJ - Existe regra específica
                    </InputLabel>
                    <Select
                      {...field}
                      required disabled={bankCommunicationIsValidated}
                      labelId="specific-payment-rule-label"
                      label="Outro CNPJ - Existe regra específica"
                    >
                      {specificPaymentRuleOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.specificPaymentRule?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {specificPaymentRule === "1" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="specificPaymentRuleDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={bankCommunicationIsValidated}
                      label="Descreva a Regra de Negócio"
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.specificPaymentRuleDescription}
                      helperText={
                        errors.specificPaymentRuleDescription?.message
                      }
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}
